/* ==========================================================================
   sk-drawer.js — Right-Side Cart Drawer Controller
   --------------------------------------------------------------------------
   Features:
   - open/close with body-scroll-lock + focus-trap
   - render cart items after /cart.js refresh
   - quantity +/-, remove with fade-out animation
   - loading state during cart mutations
   - error toast on API failures
   - drag-to-close on mobile (touch events)
   - shipping progress, empty state
   - cross-sell via /recommendations/products.json
   - dynamic video-gate button toggle
   - iOS safe-area inset
   - Pflanzen (13.10.2026): Lieferzeit/Versandart je Position + Hinweis bei unterschiedlichen Lieferzeiten aus
     localStorage „sk_plant_ship_v1" (geschrieben von assets/sk-plant-qm.js auf Pflanzen-Seiten); ohne Eintrag keine Ausgabe
   - Speditionsfracht (16.10.2026): Kasten im Drawer füllt assets/sk-plant-freight.js (setCart); nur Speditionsware im
     Warenkorb → Versandkosten-Leiste aus (applyShipping). Gemischt wie bisher (Shopify zählt den ganzen Warenkorb zur 29-€-Grenze).
   ========================================================================== */
(function () {
  'use strict';
  if (typeof document === 'undefined') return;

  var FREE_SHIPPING_THRESHOLD = 2900;
  var NO_FREE_SHIPPING = false;
  var LOCK_CLASS = 'sk-drawer-open';
  var CROSS_SELL_LIMIT = 3;

  // IP-based Country-Check via Cloudflare Trace.
  // Macht das Video-Gate AT-sicher: User aus Austria die auf .com landen
  // bekommen via Shopify-Markets zwar Market=DE, aber ihre echte IP ist AT.
  // Nur wenn ECHTE IP-Location === 'DE' wird das Gate final gezeigt.
  window.skActualCountry = null;
  (function fetchActualCountry() {
    try {
      fetch('/cdn-cgi/trace', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (txt) {
          if (!txt) return;
          var m = txt.match(/^loc=([A-Z]{2})/m);
          if (m) {
            window.skActualCountry = m[1];
            // Re-evaluate video gate state now that we know the actual country
            if (window.skDrawer && typeof window.skDrawer.refresh === 'function' && cachedCart) {
              updateVideoGateState(cachedCart);
            }
          }
        })
        .catch(function () {});
    } catch (_) {}
  })();

  var drawerEl = null;
  var panelEl = null;
  var bodyEl = null;
  var itemsEl = null;
  var footerEl = null;
  var ctaBlockEl = null;
  var errorToastEl = null;
  var crossSellWrap = null;
  var crossSellTrack = null;
  var errorToastTimer = null;
  var escHandler = null;
  var focusTrapHandler = null;
  var lastActiveElement = null;
  var cachedCart = null;

  function q(sel, ctx) { return (ctx || drawerEl || document).querySelector(sel); }
  function qa(sel, ctx) { return Array.prototype.slice.call((ctx || drawerEl || document).querySelectorAll(sel)); }

  function formatMoney(cents) {
    var value = (cents / 100).toFixed(2).replace('.', ',');
    return value + ' €';
  }

  function init() {
    drawerEl = document.querySelector('[data-sk-drawer]');
    if (!drawerEl) return;
    // Grenze je Land kommt aus snippets/sk-drawer.liquid (29/69/99 €)
    FREE_SHIPPING_THRESHOLD = parseInt(drawerEl.getAttribute('data-free-shipping-threshold'), 10) || FREE_SHIPPING_THRESHOLD;
    // 01.11.2026: "0" = Land mit Gewichtstarif ohne Gratis-Grenze → Gratis-Balken nie zeigen
    NO_FREE_SHIPPING = drawerEl.getAttribute('data-free-shipping-threshold') === '0';
    // Versandklassen nachgeladen (assets/sk-plant-freight.js) → Versandkosten-Leiste neu rechnen
    window.addEventListener('sk-plant-freight:cart', function () { if (cachedCart) applyShipping(cachedCart); });
    panelEl = q('[data-sk-drawer-panel]');
    bodyEl = q('[data-sk-drawer-body]');
    itemsEl = q('[data-sk-drawer-items]');
    footerEl = q('[data-sk-drawer-footer]');
    ctaBlockEl = q('[data-sk-drawer-cta-block]');

    // Set flex-order inline — robust gegen CSS-Cache-Probleme.
    // Mobile (<640): header(1) → cta-block(2) → shipping(3) → body(4) → footer(5)
    // Desktop (≥640): header(1) → shipping(2) → body(3) → cta-block(4) → footer(5)
    (function applyFlexOrder() {
      var headerEl = q('.sk-drawer__header');
      var errorEl = q('.sk-drawer__error-toast');
      var shippingEl = q('.sk-drawer__shipping');
      var bodyEl2 = q('.sk-drawer__body');
      var subtotalEl = q('.sk-drawer__subtotal');
      var metaEl = q('.sk-drawer__subtotal-meta');
      var actionsEl = q('.sk-drawer__actions');
      var trustEl = q('.sk-drawer__trust');
      function apply() {
        var isMobile = window.matchMedia('(max-width: 639px)').matches;
        if (headerEl) headerEl.style.order = '1';
        if (errorEl) errorEl.style.order = '1';
        if (ctaBlockEl) ctaBlockEl.style.order = isMobile ? '2' : '4';
        if (shippingEl) shippingEl.style.order = isMobile ? '3' : '2';
        if (bodyEl2) bodyEl2.style.order = isMobile ? '4' : '3';
        // Inside CTA block: Mobile = Trust(0) → CTA(1) → Subtotal(2) → Meta(3)
        //                   Desktop = Subtotal(1) → Meta(2) → CTA(3) → Trust(4)
        if (ctaBlockEl) ctaBlockEl.style.display = 'flex';
        if (ctaBlockEl) ctaBlockEl.style.flexDirection = 'column';
        if (trustEl) trustEl.style.order = isMobile ? '0' : '4';
        if (actionsEl) actionsEl.style.order = isMobile ? '1' : '3';
        if (subtotalEl) subtotalEl.style.order = isMobile ? '2' : '1';
        if (metaEl) metaEl.style.order = isMobile ? '3' : '2';
        if (subtotalEl) subtotalEl.style.marginTop = isMobile ? '10px' : '0';
      }
      apply();
      window.matchMedia('(max-width: 639px)').addEventListener('change', apply);
    })();
    errorToastEl = q('[data-sk-drawer-error]');
    crossSellWrap = q('[data-sk-drawer-crosssell]');
    crossSellTrack = q('[data-sk-drawer-crosssell-track]');
    checkoutLabelEl = q('[data-sk-drawer-checkout-label]');
    initVideoGate();

    var checkoutForm = q('.sk-drawer__checkout-form');
    if (checkoutForm) checkoutForm.addEventListener('submit', onCheckoutSubmit);

    // Close handlers
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-sk-drawer-close], [data-sk-drawer-backdrop], [data-sk-drawer-close-link]')) {
        close();
      }
    });
    drawerEl.addEventListener('click', onDrawerClick);

    // Drag-to-close (mobile bottom-sheet)
    setupDragToClose();
  }

  function onDrawerClick(e) {
    var target = e.target;
    var incBtn = target.closest('[data-sk-drawer-qty-increment]');
    if (incBtn) {
      var item = incBtn.closest('[data-sk-drawer-item]');
      var valEl = item.querySelector('[data-sk-drawer-qty-value]');
      var newQty = parseInt(valEl.textContent || '1', 10) + 1;
      changeQuantity(item.dataset.lineKey, newQty, incBtn, item);
      return;
    }
    var decBtn = target.closest('[data-sk-drawer-qty-decrement]');
    if (decBtn) {
      var item2 = decBtn.closest('[data-sk-drawer-item]');
      var valEl2 = item2.querySelector('[data-sk-drawer-qty-value]');
      var curQty = parseInt(valEl2.textContent || '1', 10);
      var newQty2 = Math.max(0, curQty - 1);
      changeQuantity(item2.dataset.lineKey, newQty2, decBtn, item2);
      return;
    }
    var removeBtn = target.closest('[data-sk-drawer-remove]');
    if (removeBtn) {
      var item3 = removeBtn.closest('[data-sk-drawer-item]');
      // Fade-out animation, then remove
      item3.classList.add('sk-drawer__item--removing');
      setTimeout(function () {
        changeQuantity(item3.dataset.lineKey, 0, removeBtn, item3);
      }, 180);
      return;
    }
  }

  function changeQuantity(lineKey, quantity, btnEl, itemEl) {
    if (itemEl) itemEl.classList.add('sk-drawer__item--loading');
    if (btnEl) {
      btnEl.disabled = true;
      btnEl.classList.add('is-loading');
    }
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('change failed: ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        cachedCart = cart;
        renderCart(cart);
        updateCartCountBadges(cart.item_count);
        dispatchCartChanged(cart);
      })
      .catch(function (err) {
        console.error('[sk-drawer] quantity change failed', err);
        if (itemEl) itemEl.classList.remove('sk-drawer__item--loading', 'sk-drawer__item--removing');
        if (btnEl) {
          btnEl.disabled = false;
          btnEl.classList.remove('is-loading');
        }
        showError(i18n('error', 'Aktualisierung fehlgeschlagen. Bitte erneut versuchen.'));
      });
  }

  /* ---------- Pflanzen: Lieferzeit je Position (keine Lieferantennamen, nur Texte der Produktseite) ---------- */
  var plantShip = null;
  function readPlantShip() {
    try { plantShip = JSON.parse(window.localStorage.getItem('sk_plant_ship_v1') || 'null'); } catch (_) { plantShip = null; }
    if (!plantShip || typeof plantShip !== 'object' || !plantShip.v) plantShip = null;
    return plantShip;
  }
  function plantEntry(item) {
    return plantShip && item && plantShip.v[String(item.variant_id)] || null;
  }
  function plantLine(item) {
    var e = plantEntry(item);
    if (!e) return '';
    var parts = [];
    if (e.t) parts.push(escapeHtml(e.t));
    if (e.s === 'spedition' && plantShip.l && plantShip.l.sped) parts.push(escapeHtml(plantShip.l.sped));
    if (e.a) parts.push(escapeHtml(e.a));
    if (!parts.length) return '';
    return '<span class="sk-drawer__item-plant" data-sk-drawer-plant>' +
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h11v10H3zM14 9h4l3 3v4h-7"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg>' +
      '<span>' + parts.join(' · ') + '</span></span>';
  }
  function plantNote(cart) {
    if (!plantShip || !cart || !cart.items || !plantShip.l || !plantShip.l.note) return '';
    var sigs = {};
    var plants = 0;
    cart.items.forEach(function (it) {
      var e = plantEntry(it);
      if (e) plants++;
      sigs[e ? 'p:' + e.k : 'std'] = true;
    });
    if (!plants || Object.keys(sigs).length < 2) return '';
    return '<p class="sk-drawer__plant-note" role="note" data-sk-drawer-plant-note>' + escapeHtml(plantShip.l.note) + '</p>';
  }
  function plantStyles() {
    if (document.getElementById('sk-drawer-plant-css')) return;
    var st = document.createElement('style');
    st.id = 'sk-drawer-plant-css';
    st.textContent = '.sk-drawer__item-plant{display:flex;align-items:flex-start;gap:4px;margin:2px 0 4px;font-size:12px;line-height:1.35;color:var(--sk-text-secondary)}' +
      '.sk-drawer__item-plant svg{flex:0 0 auto;margin-top:1px;color:var(--sk-secondary)}' +
      '.sk-drawer__plant-note{margin:0 0 var(--sk-space-sm,8px);padding:8px 12px;background:var(--sk-bg-light);border-inline-start:3px solid var(--sk-accent);border-radius:var(--sk-radius-sm,8px);font-size:12.5px;line-height:1.45;color:var(--sk-text)}';
    document.head.appendChild(st);
  }

  function renderCart(cart) {
    if (!cart) return;
    if (readPlantShip()) plantStyles();
    var countEl = q('[data-sk-drawer-count]');
    if (countEl) countEl.textContent = cart.item_count;
    var subtotalEl = q('[data-sk-drawer-subtotal]');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    if (window.skPlantFreight && typeof window.skPlantFreight.setCart === 'function') {
      try { window.skPlantFreight.setCart(cart); } catch (_) {}
    }
    applyShipping(cart);
    if (cart.item_count === 0) {
      itemsEl.innerHTML = renderEmptyState();
      if (footerEl) footerEl.setAttribute('hidden', '');
      if (ctaBlockEl) ctaBlockEl.setAttribute('hidden', '');
    } else {
      itemsEl.innerHTML = plantNote(cart) + cart.items.map(renderItem).join('');
      if (footerEl) footerEl.removeAttribute('hidden');
      if (ctaBlockEl) ctaBlockEl.removeAttribute('hidden');
    }
    updateVideoGateState(cart);
    // Load cross-sell based on last item
    if (cart.items.length > 0) {
      loadCrossSell(cart.items[cart.items.length - 1].product_id);
    } else if (crossSellWrap) {
      crossSellWrap.setAttribute('hidden', '');
    }
  }

  // Grundpreis (PAngV, 14.10.2026) je Position, Format aus window.skUnitPrice (snippets/unit-price-fix.liquid)
  function unitLine(item) {
    if (!window.skUnitPrice || !item || !item.unit_price_measurement) return '';
    var t = window.skUnitPrice.text(item.unit_price_measurement, item.unit_price, item.variant_title, false);
    return t ? '<span class="sk-drawer__item-unit">' + escapeHtml(t) + '</span>' : '';
  }

  function renderItem(item) {
    var img = item.image
      ? '<img src="' + escapeHtml(item.image.replace(/\.(jpg|jpeg|png|webp|gif)/i, '_160x$&')) + '" alt="" width="80" height="80" loading="lazy">'
      : '<span class="sk-drawer__item-thumb-placeholder" aria-hidden="true"></span>';
    var variant = (item.variant_title && item.variant_title !== 'Default Title')
      ? '<span class="sk-drawer__item-variant">' + escapeHtml(item.variant_title) + '</span>'
      : '';
    var videoIds = window.skVideoProductIds || [];
    var itemHasVideo = videoIds.indexOf(item.product_id) !== -1;
    var watchedList = (function(){ try { return JSON.parse(localStorage.getItem('watchingCompleted') || '[]'); } catch(_){ return []; } })();
    var itemWatched = itemHasVideo && watchedList.indexOf(String(item.product_id)) !== -1;
    var videoBadge = itemHasVideo
      ? (itemWatched
          ? '<span class="sk-drawer__item-video-badge sk-drawer__item-video-badge--done">' +
              '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>' +
              'Video angesehen' +
            '</span>'
          : '<span class="sk-drawer__item-video-badge">' +
              '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>' +
              i18n('required', 'Abgabegespräch erforderlich') +
            '</span>'
        )
      : '';
    var articleClass = 'sk-drawer__item' + (itemHasVideo ? ' sk-drawer__item--video' : '') + (itemWatched ? ' sk-drawer__item--video-done' : '');
    var videoAttr = itemHasVideo ? ' data-sk-video-required="true"' : '';
    return '' +
      '<article class="' + articleClass + '" data-sk-drawer-item data-line-key="' + escapeHtml(item.key) + '"' + videoAttr + '>' +
        '<a href="' + escapeHtml(item.url) + '" class="sk-drawer__item-thumb" aria-label="' + escapeHtml(item.product_title) + '">' + img + '</a>' +
        '<div class="sk-drawer__item-body">' +
          '<a href="' + escapeHtml(item.url) + '" class="sk-drawer__item-title">' + escapeHtml(item.product_title) + '</a>' +
          variant +
          /* Grundpreis je Warenkorb-Zeile entfernt (Philipp 31.10.2026); Cross-Sell-Karten behalten ihn (Angebot, PAngV) */
          plantLine(item) +
          videoBadge +
          '<div class="sk-drawer__item-row">' +
            '<div class="sk-drawer__quantity" data-sk-drawer-quantity>' +
              '<button type="button" class="sk-drawer__quantity-btn" data-sk-drawer-qty-decrement aria-label="' + escapeHtml(i18n('qty-less', 'Weniger')) + '">−</button>' +
              '<span class="sk-drawer__quantity-value" data-sk-drawer-qty-value>' + item.quantity + '</span>' +
              '<button type="button" class="sk-drawer__quantity-btn" data-sk-drawer-qty-increment aria-label="' + escapeHtml(i18n('qty-more', 'Mehr')) + '">+</button>' +
            '</div>' +
            '<span class="sk-drawer__item-price">' + formatMoney(item.final_line_price) + '</span>' +
          '</div>' +
        '</div>' +
        '<button type="button" class="sk-drawer__item-remove" data-sk-drawer-remove aria-label="' + escapeHtml(i18n('remove', 'Entfernen')) + '">' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 012-2h2a2 2 0 012 2v2"/></svg>' +
        '</button>' +
      '</article>';
  }

  function renderEmptyState() {
    return '' +
      '<div class="sk-drawer__empty" data-sk-drawer-empty>' +
        '<div class="sk-drawer__empty-icon">' +
          '<svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18l-2 13H5L3 6z"/><path d="M16 10a4 4 0 01-8 0"/></svg>' +
        '</div>' +
        '<p class="sk-drawer__empty-text">' + escapeHtml(i18n('empty', 'Dein Warenkorb ist leer')) + '</p>' +
        '<a href="/collections/beliebte-produkte" class="sk-drawer__empty-cta" data-sk-drawer-close-link>' + escapeHtml(i18n('empty-cta', 'Jetzt einkaufen')) + '</a>' +
      '</div>';
  }

  // Nur Speditionsware (Varianten-Metafeld pflanze.versandklasse, Daten aus assets/sk-plant-freight.js) → Leiste aus.
  // Gemischt bleibt die Basis der ganze Warenkorb – so rechnet auch Shopify die Paket-Rate (Checkout-Stichprobe 16.10.).
  function applyShipping(cart) {
    if (!cart) return;
    var off = false;
    var sp = null;
    if (window.skPlantFreight && typeof window.skPlantFreight.split === 'function') {
      try { sp = window.skPlantFreight.split(cart); off = !!sp.onlyFreight; } catch (_) { off = false; sp = null; }
    }
    var shipping = q('[data-sk-drawer-shipping]');
    if (shipping) shipping.hidden = off || NO_FREE_SHIPPING;
    updateShippingProgress(cart.total_price);
    applyTrust(sp);
  }

  // Lieferzeit in der Trust-Zeile (16.10.2026): Speditions-Pflanzen → „Pflanzen: 5–8 Werktage" bzw. gemischt
  // „…, übrige Artikel: 1–2 Werktage". Vorlagen als data-Attribute aus snippets/sk-drawer.liquid (Server-Stand: sk-plant-ship-time).
  // Ohne Klassen-/Lieferzeit-Daten (sk-plant-freight.js lädt noch) bleibt der Server-Text stehen.
  function applyTrust(sp) {
    var el = q('[data-sk-trust-ship]');
    if (!el || !sp || !sp.known || sp.daysKnown === false) return;
    var sep = q('[data-sk-trust-ship-sep]');
    var txt = el.getAttribute('data-std') || '';
    if (sp.hasFreight) {
      var tpl = el.getAttribute(sp.onlyFreight ? 'data-plants' : 'data-mixed') || '';
      var days = '';
      if (sp.dmax > 0) {
        days = (sp.dmin === sp.dmax ? el.getAttribute('data-days-one') : el.getAttribute('data-days')) || '';
        days = days.replace('%MIN%', String(sp.dmin)).replace('%MAX%', String(sp.dmax));
      }
      txt = tpl && days ? tpl.replace('%VALUE%', days) : '';
    }
    el.textContent = txt;
    el.hidden = !txt;
    if (sep) sep.hidden = !txt;
  }

  function updateShippingProgress(total) {
    var pct = Math.min(100, Math.round((total / FREE_SHIPPING_THRESHOLD) * 100));
    var bar = q('[data-sk-drawer-shipping-bar]');
    if (bar) bar.style.width = pct + '%';
    // Move truck along progress
    var truck = q('[data-sk-drawer-shipping-truck]');
    if (truck) truck.style.left = pct + '%';
    // Toggle achieved class (adds celebration animation + swaps gift icon to check)
    var shipping = q('[data-sk-drawer-shipping]');
    if (shipping) {
      var wasAchieved = shipping.classList.contains('sk-drawer__shipping--achieved');
      var isAchieved = total >= FREE_SHIPPING_THRESHOLD;
      if (isAchieved && !wasAchieved) {
        shipping.classList.add('sk-drawer__shipping--achieved');
      } else if (!isAchieved && wasAchieved) {
        shipping.classList.remove('sk-drawer__shipping--achieved');
      }
      if (drawerEl) drawerEl.setAttribute('data-shipping-achieved', isAchieved ? 'true' : 'false');
    }
    var textEl = q('[data-sk-drawer-shipping-text]');
    if (!textEl) return;
    if (total >= FREE_SHIPPING_THRESHOLD) {
      textEl.innerHTML = '<strong>' + escapeHtml(i18n('free-achieved', 'Du erhältst kostenlosen Versand')) + '</strong> 🎉';
    } else {
      var remaining = FREE_SHIPPING_THRESHOLD - total;
      textEl.innerHTML = escapeHtml(i18n('free-prefix', 'Noch')) + ' <strong data-sk-drawer-shipping-remaining>' + formatMoney(remaining) + '</strong> ' + escapeHtml(i18n('free-suffix', 'bis zum kostenlosen Versand'));
    }
  }

  function updateCartCountBadges(count) {
    var n = count > 0 ? Number(count) : 0;
    // Horizon: Header-Bubble (snippets/cart-bubble.liquid) direkt aktualisieren – Ziffer in .cart-bubble__text-count,
    // Sichtbarkeit über .visually-hidden (Bubble) und .hidden (Ziffer). Ab 100 zeigt Horizon keine Ziffer.
    qa(".cart-bubble", document).forEach(function (bubble) {
      var num = bubble.querySelector(".cart-bubble__text-count");
      if (num) {
        num.textContent = n > 0 && n < 100 ? String(n) : "";
        num.classList.toggle("hidden", n === 0);
      }
      bubble.classList.toggle("visually-hidden", n === 0);
    });
    qa("cart-icon", document).forEach(function (icon) {
      icon.classList.toggle("header-actions__cart-icon--has-cart", n > 0);
    });
    window.skCart = window.skCart || {};
    window.skCart.itemCount = n;
  }

  // ─── Inline Video Gate ─────────────────────────────────────────────────
  var videoGateEl = null;
  var videoGatePillEl = null;
  var videoGateBodyEl = null;
  var videoGateTabsEl = null;
  var videoGatePlayerEl = null;
  var videoGateTitleEl = null;
  var videoGateMetaEl = null;
  var checkoutLabelEl = null;

  function getWatched() {
    try { return JSON.parse(localStorage.getItem('watchingCompleted') || '[]'); }
    catch (_) { return []; }
  }
  function setWatched(arr) {
    try { localStorage.setItem('watchingCompleted', JSON.stringify(arr)); } catch (_) {}
  }
  function markWatched(pid) {
    var arr = getWatched();
    if (arr.indexOf(pid) === -1) { arr.push(pid); setWatched(arr); }
  }

  function getRelevantVideos(cart) {
    var data = window.skVideoData || {};
    var seen = {};
    var out = [];
    if (!cart || !cart.items) return out;
    cart.items.forEach(function (item) {
      var pid = String(item.product_id);
      if (seen[pid]) return;
      if (data[pid] && data[pid].url) {
        seen[pid] = true;
        out.push({ id: pid, url: data[pid].url, title: data[pid].title || item.product_title, thumb: data[pid].thumb || item.image });
      }
    });
    return out;
  }

  function updateVideoGateState(cart) {
    if (!drawerEl) return;

    // Reset CTA-Label immer auf Default — wird unten ggf. auf "Video zuerst ansehen" überschrieben
    if (checkoutLabelEl) checkoutLabelEl.textContent = i18n('checkout', 'Zur Kasse');

    // Hard Guard: Video-Gate NUR für DE-Customer. Gesetzlich-vorgeschriebenes Abgabegespräch
    // gilt nur in Deutschland (EU-Verordnung 528/2012 umgesetzt via ChemG).
    // Doppelter Check: (1) Shopify-Markets-Locale = DE UND (2) echte IP-Location = DE.
    // Damit werden z.B. AT-User auf silberkrafto.shop ausgeschlossen (Market=DE, IP=AT).
    var notDEmarket = window.skCountryIsDE === false;
    var notDEactual = window.skActualCountry && window.skActualCountry !== 'DE';
    if (notDEmarket || notDEactual) {
      drawerEl.setAttribute('data-cart-has-video', 'false');
      if (window.skCart) window.skCart.hasVideo = false;
      if (videoGateEl) videoGateEl.setAttribute('hidden', '');
      return;
    }

    if (window.skCart && window.skCart.isReturning) {
      drawerEl.setAttribute('data-cart-has-video', 'false');
      window.skCart.hasVideo = false;
      if (videoGateEl) videoGateEl.setAttribute('hidden', '');
      return;
    }

    var videos = getRelevantVideos(cart);
    var hasVideo = videos.length > 0;
    var watched = getWatched();
    var pendingVideos = videos.filter(function (v) { return watched.indexOf(v.id) === -1; });
    var allWatched = hasVideo && pendingVideos.length === 0;

    drawerEl.setAttribute('data-cart-has-video', (hasVideo && !allWatched) ? 'true' : 'false');
    window.skCart = window.skCart || {};
    window.skCart.hasVideo = hasVideo && !allWatched;

    if (!videoGateEl) return;
    if (!hasVideo) {
      videoGateEl.setAttribute('hidden', '');
      if (videoGateBodyEl) videoGateBodyEl.setAttribute('hidden', '');
      if (videoGatePillEl) videoGatePillEl.setAttribute('aria-expanded', 'false');
      return;
    }

    videoGateEl.removeAttribute('hidden');
    videoGateEl.setAttribute('data-sk-video-gate-state', allWatched ? 'done' : 'pending');

    if (videoGateTitleEl) {
      videoGateTitleEl.textContent = allWatched
        ? (i18n('video_gate_done', 'Abgabegespräch abgeschlossen'))
        : (i18n('video_gate_title', 'Kurze Produkt-Einweisung'));
    }
    if (videoGateMetaEl) {
      if (allWatched) {
        videoGateMetaEl.textContent = i18n('video_gate_watched_meta', 'Du kannst zur Kasse gehen');
      } else if (videos.length === 1) {
        videoGateMetaEl.textContent = i18n('video_gate_meta_single', 'Eine 45-Sekunden-Einweisung vor der Kasse');
      } else {
        videoGateMetaEl.textContent = (i18n('video_gate_meta_multi', '{{ count }} kurze Einweisungen vor der Kasse')).replace('{{ count }}', videos.length);
      }
    }

    renderVideoTabs(videos, watched);

    if (checkoutLabelEl) {
      checkoutLabelEl.textContent = allWatched || !hasVideo
        ? i18n('checkout', 'Zur Kasse')
        : i18n('video_gate_blocked_cta', 'Video zuerst ansehen');
    }

    if (allWatched) {
      if (videoGateBodyEl) videoGateBodyEl.setAttribute('hidden', '');
      if (videoGatePillEl) videoGatePillEl.setAttribute('aria-expanded', 'false');
    }
  }

  function renderVideoTabs(videos, watched) {
    if (!videoGateTabsEl) return;
    if (videos.length <= 1) {
      videoGateTabsEl.setAttribute('hidden', '');
      videoGateTabsEl.innerHTML = '';
      if (videos[0] && videoGatePlayerEl && videoGatePlayerEl.dataset.pid !== videos[0].id) {
        setVideoSource(videos[0]);
      }
      return;
    }
    videoGateTabsEl.removeAttribute('hidden');
    var currentPid = videoGatePlayerEl ? videoGatePlayerEl.dataset.pid : null;
    if (!currentPid || !videos.some(function (v) { return v.id === currentPid; })) {
      var firstPending = videos.find(function (v) { return watched.indexOf(v.id) === -1; }) || videos[0];
      setVideoSource(firstPending);
      currentPid = firstPending.id;
    }
    videoGateTabsEl.innerHTML = videos.map(function (v) {
      var isDone = watched.indexOf(v.id) !== -1;
      var isActive = v.id === currentPid;
      return '<button type="button" class="sk-drawer__video-gate-tab" data-sk-video-gate-tab data-pid="' + escapeHtml(v.id) + '" data-src="' + escapeHtml(v.url) + '" aria-pressed="' + (isActive ? 'true' : 'false') + '" data-watched="' + (isDone ? 'true' : 'false') + '">' +
        (v.thumb ? '<img src="' + escapeHtml(v.thumb) + '" alt="" width="24" height="24">' : '') +
        '<span>' + escapeHtml((v.title || '').length > 20 ? v.title.slice(0, 18) + '…' : (v.title || '')) + '</span>' +
      '</button>';
    }).join('');
  }

  function setVideoSource(video) {
    if (!videoGatePlayerEl || !video) return;
    if (videoGatePlayerEl.dataset.pid === video.id) return;
    videoGatePlayerEl.dataset.pid = video.id;
    videoGatePlayerEl.src = video.url;
    try { videoGatePlayerEl.load(); } catch (_) {}
  }

  function formatVideoTime(s) {
    if (!s || !isFinite(s)) return '0:00';
    var m = Math.floor(s / 60);
    var r = Math.floor(s % 60);
    return m + ':' + (r < 10 ? '0' : '') + r;
  }

  function initVideoGate() {
    videoGateEl = q('[data-sk-video-gate]');
    if (!videoGateEl) return;
    videoGatePillEl = q('[data-sk-video-gate-toggle]');
    videoGateBodyEl = q('[data-sk-video-gate-body]');
    videoGateTabsEl = q('[data-sk-video-gate-tabs]');
    videoGatePlayerEl = q('[data-sk-video-gate-player]');
    videoGateTitleEl = q('[data-sk-video-gate-title]');
    videoGateMetaEl = q('[data-sk-video-gate-meta]');

    // Custom controls wiring
    var controlsEl = q('[data-sk-video-controls]');
    var playPauseBtn = q('[data-sk-video-playpause]');
    var muteBtn = q('[data-sk-video-mute]');
    var progressFillEl = q('[data-sk-video-progress]');
    var timeEl = q('[data-sk-video-time]');
    var playOverlayEl = q('[data-sk-video-play-overlay]');

    function syncPlayState() {
      if (!controlsEl) return;
      controlsEl.setAttribute('data-state', videoGatePlayerEl.paused ? 'paused' : 'playing');
      if (playOverlayEl) {
        if (videoGatePlayerEl.paused) playOverlayEl.removeAttribute('hidden');
        else playOverlayEl.setAttribute('hidden', '');
      }
      if (playPauseBtn) playPauseBtn.setAttribute('aria-label', videoGatePlayerEl.paused ? 'Abspielen' : 'Pausieren');
    }
    function syncMuteState() {
      if (!controlsEl) return;
      controlsEl.setAttribute('data-muted', videoGatePlayerEl.muted ? 'true' : 'false');
      if (muteBtn) muteBtn.setAttribute('aria-label', videoGatePlayerEl.muted ? 'Ton einschalten' : 'Stumm schalten');
    }

    if (videoGatePlayerEl) {
      videoGatePlayerEl.addEventListener('play', syncPlayState);
      videoGatePlayerEl.addEventListener('pause', syncPlayState);
      videoGatePlayerEl.addEventListener('volumechange', syncMuteState);
      videoGatePlayerEl.addEventListener('timeupdate', function () {
        if (progressFillEl && videoGatePlayerEl.duration) {
          progressFillEl.style.width = ((videoGatePlayerEl.currentTime / videoGatePlayerEl.duration) * 100).toFixed(2) + '%';
        }
        if (timeEl) timeEl.textContent = formatVideoTime(videoGatePlayerEl.currentTime) + ' / ' + formatVideoTime(videoGatePlayerEl.duration);
      });
      videoGatePlayerEl.addEventListener('loadedmetadata', function () {
        if (timeEl) timeEl.textContent = '0:00 / ' + formatVideoTime(videoGatePlayerEl.duration);
      });
      videoGatePlayerEl.addEventListener('ended', function () {
        if (progressFillEl) progressFillEl.style.width = '100%';
        syncPlayState();
      });

      // Click on video itself = toggle play
      videoGatePlayerEl.addEventListener('click', function () {
        if (videoGatePlayerEl.paused) videoGatePlayerEl.play();
        else videoGatePlayerEl.pause();
      });
    }

    if (playPauseBtn) {
      playPauseBtn.addEventListener('click', function () {
        if (videoGatePlayerEl.paused) videoGatePlayerEl.play();
        else videoGatePlayerEl.pause();
      });
    }
    if (muteBtn) {
      muteBtn.addEventListener('click', function () {
        videoGatePlayerEl.muted = !videoGatePlayerEl.muted;
      });
    }
    if (playOverlayEl) {
      playOverlayEl.addEventListener('click', function (e) {
        e.stopPropagation();
        videoGatePlayerEl.play();
      });
    }

    if (videoGatePillEl) {
      videoGatePillEl.addEventListener('click', function () {
        var isDone = videoGateEl.getAttribute('data-sk-video-gate-state') === 'done';
        if (isDone) return;
        var expanded = videoGatePillEl.getAttribute('aria-expanded') === 'true';
        toggleVideoGate(!expanded);
      });
    }

    if (videoGatePlayerEl) {
      // Seek-Block: User darf nicht vorspulen. Wir tracken maxWatchedTime
      // und clampen forward-seeks. Rückwärts-seek bleibt erlaubt.
      var maxWatchedByPid = {};
      videoGatePlayerEl.addEventListener('seeking', function () {
        var pid = videoGatePlayerEl.dataset.pid;
        if (!pid) return;
        var max = maxWatchedByPid[pid] || 0;
        if (videoGatePlayerEl.currentTime > max + 0.5) {
          videoGatePlayerEl.currentTime = max;
        }
      });
      videoGatePlayerEl.addEventListener('timeupdate', function () {
        var pid = videoGatePlayerEl.dataset.pid;
        if (!pid) return;
        if (!videoGatePlayerEl.seeking) {
          if (!maxWatchedByPid[pid] || videoGatePlayerEl.currentTime > maxWatchedByPid[pid]) {
            maxWatchedByPid[pid] = videoGatePlayerEl.currentTime;
          }
        }
      });

      // Speed-Buttons (1x/2x/4x/8x)
      drawerEl.addEventListener('click', function (e) {
        var sb = e.target.closest('[data-sk-video-speed]');
        if (!sb) return;
        var rate = parseFloat(sb.getAttribute('data-sk-video-speed')) || 1;
        videoGatePlayerEl.playbackRate = rate;
        qa('[data-sk-video-speed]').forEach(function (b) { b.classList.toggle('is-active', b === sb); });
      });

      videoGatePlayerEl.addEventListener('loadedmetadata', function () {
        if (!videoGateMetaEl) return;
        if (drawerEl.getAttribute('data-cart-has-video') !== 'true') return;
        var videos = getRelevantVideos(cachedCart);
        if (videos.length > 1) return; // multi-video: keep count label
        var d = videoGatePlayerEl.duration;
        if (!d || !isFinite(d)) return;
        var mins = Math.floor(d / 60);
        var secs = Math.round(d % 60);
        var label = mins > 0 ? (mins + ':' + (secs < 10 ? '0' : '') + secs + ' min') : (secs + ' Sek');
        var tmpl = i18n('video_gate_meta_single_duration', '{{ duration }} Einweisung vor der Kasse');
        videoGateMetaEl.textContent = tmpl.replace('{{ duration }}', label);
      });
      videoGatePlayerEl.addEventListener('timeupdate', function () {
        var pid = videoGatePlayerEl.dataset.pid;
        if (!pid) return;
        if (videoGatePlayerEl.duration && videoGatePlayerEl.currentTime / videoGatePlayerEl.duration >= 0.95) {
          if (getWatched().indexOf(pid) === -1) {
            markWatched(pid);
            onVideoCompleted(pid);
          }
        }
      });
      videoGatePlayerEl.addEventListener('ended', function () {
        var pid = videoGatePlayerEl.dataset.pid;
        if (pid && getWatched().indexOf(pid) === -1) {
          markWatched(pid);
        }
        onVideoCompleted(pid);
      });
    }

    drawerEl.addEventListener('click', function (e) {
      var tab = e.target.closest('[data-sk-video-gate-tab]');
      if (!tab) return;
      var pid = tab.getAttribute('data-pid');
      var src = tab.getAttribute('data-src');
      setVideoSource({ id: pid, url: src });
      qa('[data-sk-video-gate-tab]').forEach(function (t) { t.setAttribute('aria-pressed', t === tab ? 'true' : 'false'); });
      try { videoGatePlayerEl.play(); } catch (_) {}
    });
  }

  function toggleVideoGate(open) {
    if (!videoGateEl || !videoGateBodyEl || !videoGatePillEl) return;
    if (open) {
      videoGateBodyEl.removeAttribute('hidden');
      videoGatePillEl.setAttribute('aria-expanded', 'true');
      if (videoGatePlayerEl && !videoGatePlayerEl.src) {
        var videos = getRelevantVideos(cachedCart);
        var watched = getWatched();
        var first = videos.find(function (v) { return watched.indexOf(v.id) === -1; }) || videos[0];
        if (first) setVideoSource(first);
      }
    } else {
      videoGateBodyEl.setAttribute('hidden', '');
      videoGatePillEl.setAttribute('aria-expanded', 'false');
      if (videoGatePlayerEl) { try { videoGatePlayerEl.pause(); } catch (_) {} }
    }
  }

  function onVideoCompleted(pid) {
    var videos = getRelevantVideos(cachedCart);
    var watched = getWatched();
    renderVideoTabs(videos, watched);
    // Re-render cart items so the badge flips from "erforderlich" → "angesehen"
    if (cachedCart) renderCart(cachedCart);
    var pending = videos.filter(function (v) { return watched.indexOf(v.id) === -1; });
    if (pending.length > 0) {
      setVideoSource(pending[0]);
      try { videoGatePlayerEl.play(); } catch (_) {}
    } else {
      updateVideoGateState(cachedCart);
    }
  }

  function onCheckoutSubmit(e) {
    if (drawerEl && drawerEl.getAttribute('data-cart-has-video') === 'true') {
      e.preventDefault();
      if (videoGateEl && videoGateBodyEl) {
        toggleVideoGate(true);
        try { videoGateEl.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (_) {}
        try { if (videoGatePlayerEl && videoGatePlayerEl.paused) videoGatePlayerEl.play(); } catch (_) {}
      }
    }
  }

  function dispatchCartChanged(cart) {
    window.dispatchEvent(new CustomEvent('sk:cart-changed', { detail: { cart: cart } }));
  }

  // Feuert Shopifys Standard-"cart_viewed" Event via Web Pixel API.
  // Wird von Shopify Analytics, Klaviyo, Meta/TikTok/GA4-Pixel und Apps konsumiert.
  function fireCartViewed(cart) {
    if (!cart || !cart.item_count) return;
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('cart_viewed', { cart: cart });
      }
    } catch (_) {}
    try {
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib && typeof window.ShopifyAnalytics.lib.track === 'function') {
        window.ShopifyAnalytics.lib.track('Viewed Cart', {});
      }
    } catch (_) {}
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function refresh() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cachedCart = cart;
        renderCart(cart);
        updateCartCountBadges(cart.item_count);
        return cart;
      })
      .catch(function (err) {
        console.error('[sk-drawer] refresh failed', err);
        return null;
      });
  }

  // ─── Cross-Sell ────────────────────────────────────────────────────────
  // Nutzt Shopify Section-Rendering API um die Cards server-side zu rendern,
  // damit das `custom.short_title` Metafield mit einfließen kann.
  // Filter (cart-items ausschließen, limit) erfolgt in der Liquid-Section.
  var lastCrossSellId = null;

  function loadCrossSell(productId) {
    if (!crossSellWrap || !crossSellTrack) return;
    if (!productId) return;
    lastCrossSellId = productId;

    var FETCH_LIMIT = CROSS_SELL_LIMIT * 4;

    function fetchCards(intent) {
      return fetch('/recommendations/products?product_id=' + productId + '&limit=' + FETCH_LIMIT + '&intent=' + intent + '&section_id=sk-drawer-crosssell', {
        credentials: 'same-origin',
        headers: { 'Accept': 'text/html' },
      })
        .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
        .then(function (html) {
          var tmp = document.createElement('div');
          tmp.innerHTML = html.trim();
          var inner = tmp.querySelector('.shopify-section, section') || tmp;
          return inner.querySelectorAll('.sk-drawer__crosssell-card');
        });
    }

    // 2-Stage: erst "complementary" (Co-Purchase-AI, bessere Relevanz),
    // falls leer dann "related" (Tag/Type-basiert, immer Ergebnisse).
    fetchCards('complementary')
      .then(function (cards) {
        if (cards.length > 0) return cards;
        return fetchCards('related');
      })
      .then(function (cards) {
        if (!cards || !cards.length) {
          crossSellWrap.setAttribute('hidden', '');
          return;
        }
        crossSellTrack.innerHTML = '';
        cards.forEach(function (c) { crossSellTrack.appendChild(c); });
        crossSellWrap.removeAttribute('hidden');
      })
      .catch(function () {
        crossSellWrap.setAttribute('hidden', '');
      });
  }

  function renderCrossSell() {
    // Re-fetch mit letzter Product-ID — Section filtert cart-Items serverseitig
    if (lastCrossSellId) loadCrossSell(lastCrossSellId);
  }

  function i18n(key, fallback) {
    var v = drawerEl && drawerEl.getAttribute('data-i18n-' + key);
    return v || fallback;
  }
  // Cross-sell add-to-cart handler (supports direct variant OR picker-select)
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-sk-crosssell-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var vid = btn.getAttribute('data-variant-id');
    if (!vid) return;
    btn.disabled = true;
    btn.classList.add('is-loading');
    btn.classList.remove('is-added');
    fetch('/cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
      body: 'id=' + encodeURIComponent(vid) + '&quantity=1',
    })
      .then(function (r) { return r.ok ? r.json() : r.json().then(function (err) { throw err; }); })
      .then(function () {
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        refresh();
        setTimeout(function () { btn.disabled = false; btn.classList.remove('is-added'); }, 1600);
      })
      .catch(function (err) {
        console.error('[sk-drawer] crosssell add failed', err);
        btn.disabled = false;
        btn.classList.remove('is-loading', 'is-added');
        showError(i18n('error', 'Konnte nicht hinzugefügt werden.'));
      });
  });

  // ─── Error Toast ────────────────────────────────────────────────────────
  function showError(msg) {
    if (!errorToastEl) return;
    errorToastEl.textContent = msg;
    errorToastEl.removeAttribute('hidden');
    if (errorToastTimer) clearTimeout(errorToastTimer);
    errorToastTimer = setTimeout(function () {
      errorToastEl.setAttribute('hidden', '');
    }, 3500);
  }

  // ─── Focus Trap ────────────────────────────────────────────────────────
  function getFocusable() {
    if (!panelEl) return [];
    return qa('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', panelEl);
  }
  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    var f = getFocusable();
    if (!f.length) { e.preventDefault(); return; }
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
    else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
  }

  // ─── Drag-to-Close (Mobile Bottom-Sheet) ──────────────────────────────
  function setupDragToClose() {
    if (!panelEl) return;
    var startY = 0, currentY = 0, dragging = false;
    var header = q('.sk-drawer__header', panelEl);
    if (!header) return;

    function onTouchStart(e) {
      if (window.innerWidth > 640) return; // only mobile
      var t = e.touches[0];
      startY = t.clientY;
      currentY = 0;
      dragging = true;
      panelEl.style.transition = 'none';
    }
    function onTouchMove(e) {
      if (!dragging) return;
      var t = e.touches[0];
      currentY = Math.max(0, t.clientY - startY);
      panelEl.style.transform = 'translateY(' + currentY + 'px)';
    }
    function onTouchEnd() {
      if (!dragging) return;
      dragging = false;
      panelEl.style.transition = '';
      if (currentY > 120) {
        close();
      } else {
        panelEl.style.transform = '';
      }
    }
    header.addEventListener('touchstart', onTouchStart, { passive: true });
    header.addEventListener('touchmove', onTouchMove, { passive: true });
    header.addEventListener('touchend', onTouchEnd);
    header.addEventListener('touchcancel', onTouchEnd);
  }

  // ─── Open/Close ────────────────────────────────────────────────────────
  function open(opts) {
    opts = opts || {};
    if (!drawerEl) return;
    lastActiveElement = document.activeElement;
    drawerEl.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add(LOCK_CLASS);
    document.body.classList.add(LOCK_CLASS);

    refresh().then(function (cart) {
      if (drawerEl.getAttribute('data-cart-has-video') === 'true') {
        toggleVideoGate(true);
      }
      fireCartViewed(cart);
    });

    if (panelEl) setTimeout(function () { panelEl.focus(); }, 50);

    escHandler = function (e) { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', escHandler);
    focusTrapHandler = function (e) { if (drawerEl.getAttribute('aria-hidden') === 'false') trapFocus(e); };
    document.addEventListener('keydown', focusTrapHandler);
  }

  function close() {
    if (!drawerEl) return;
    drawerEl.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove(LOCK_CLASS);
    document.body.classList.remove(LOCK_CLASS);
    if (errorToastEl) errorToastEl.setAttribute('hidden', '');
    if (panelEl) panelEl.style.transform = '';
    if (escHandler) { document.removeEventListener('keydown', escHandler); escHandler = null; }
    if (focusTrapHandler) { document.removeEventListener('keydown', focusTrapHandler); focusTrapHandler = null; }
    if (lastActiveElement && typeof lastActiveElement.focus === 'function') {
      try { lastActiveElement.focus(); } catch (_) {}
    }
  }

  window.skDrawer = { open: open, close: close, refresh: refresh };

  function autoOpenFromUrl() {
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.get('sk_drawer_open') === '1') {
        params.delete('sk_drawer_open');
        var newSearch = params.toString();
        history.replaceState(null, '', window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash);
        setTimeout(function () { open(); }, 150);
      }
    } catch (_) {}
  }

  function bootstrap() {
    init();
    autoOpenFromUrl();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootstrap);
  else bootstrap();
})();
